import { Grid, Typography, Paper, Button, useTheme, useMediaQuery } from '@mui/material';
import { DialogContent, DialogTitle, Chip } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { SettingsOverscanRounded } from '@mui/icons-material';
import StateManager from '../../../../Global/StateManager';
import { Field } from '../../../../Global/Fields';
import { HtmlContent, PicturesCarousel, FromNow } from '../../../../Global/Components';
import { RoundedDialog, StandardDialogActions } from '../../../../Global/Components';
import GridSection from '../../components/GridSection';
import axios from 'axios';
import moment from 'moment';
import { isArray, isEmpty, isFunction } from 'lodash';
import { styled } from '@mui/material/styles';
import FormStyleFieldTable from '../../../../Global/Fields/FormStyleFieldTable';
import FieldDataGrid from '../../../../Global/Fields/FieldDataGrid';

const SectionContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2, 1),
  },
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
  borderRadius: theme.spacing(2),
}));

export default function Section({
  entry,
  section,
  sectionIndex,
  editable,
  hiddenItems,
  autoSave = true,
  onCalculationUpdate,
  onFilledTablesUpdate,
  onHiddenUpdate,
  allCreatedActions,
  onCreatedAction,
  onDeletedAction,
  executedActions,
  onExecutedActionDelete,
  onAssetChange,
}) {
  const [saving, setSaving] = useState('');
  const [items, setItems] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const defaultDueDate = moment().add(1, 'week').set({ minutes: 0, seconds: 0, milliseconds: 0 });

  const theme = useTheme();
  const smallDevices = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (isArray(section?.items)) {
      setItems(section.items);
    }
    if (isArray(section.table?.rows)) {
      setRows(section.table.rows);
    }
    if (isArray(section.table?.summary)) {
      setSummary(section.table.summary);
    }
  }, [section]);

  function onSave(fieldId, value) {
    const body = {
      entryId: entry._id,
      sectionId: section.id,
      fieldId,
      value,
    };

    if (!autoSave) {
      return;
    }

    setSaving('Saving...');
    setSavedAt(null);

    axios
      .post('/forms/entries/saveEntryField', body)
      .then(({ data }) => {
        const index = items.findIndex((x) => x.id === data.fieldId);
        if (index > -1) {
          items[index].userId = data.userId;
          items[index].editedAt = data.editedAt;
          items[index].filledValue = value;
        }

        if (!isEmpty(data.calculated) && isArray(data.calculated)) {
          onCalculationUpdate(data.calculated);
        }

        if (
          !isEmpty(data.filledTablesUpdates) &&
          isArray(data.filledTablesUpdates) &&
          isFunction(onFilledTablesUpdate)
        ) {
          onFilledTablesUpdate(data.filledTablesUpdates);
        }

        onHiddenUpdate(data);

        setSaving('Saved');
        setSavedAt(new Date());
        if (value.valueType === 'asset') {
          onAssetChange(value.value);
        }
      })
      .catch(() => {
        setSaving('Failed to save the value');
        StateManager.setErrorAlert('Failed to save the value');
      });
  }

  function onRowsSave(rows) {
    const body = {
      entryId: entry._id,
      sectionId: section.id,
      rows,
    };

    setRows(rows);

    if (!autoSave) {
      return;
    }

    setSaving('Saving...');

    axios
      .post('/forms/entries/saveEntryTable', body)
      .then(({ data }) => {
        if (isArray(data.updatedRows)) {
          setRows(data.updatedRows);
        }
        if (isArray(data.summary)) {
          setSummary(data.summary);
        }

        setSaving('Saved');
      })
      .catch(() => {
        setSaving('Failed to save the table');
        StateManager.setErrorAlert('Failed to save the table');
      });
  }

  const itemsToRender = useMemo(
    () => items.filter((x) => !x.conditionalFieldId).filter(({ id }) => !hiddenItems?.includes(id)),
    [items, hiddenItems],
  );

  if (!section) return null;

  return (
    <SectionContainer container item>
      <Grid container component={SectionPaper} elevation={3} id={section.id}>
        <Grid container item sx={{ py: 1 }} alignItems="center">
          <Typography variant="h5">{section.title || `Section #${sectionIndex + 1}`}</Typography>
          {editable && (
            <FromNow
              prefix={saving}
              value={savedAt}
              color="textSecondary"
              style={{ fontSize: '0.75rem', marginLeft: 'auto' }}
            />
          )}
        </Grid>

        <Grid container item sx={{ py: 1 }}>
          {section.type !== 'grid' &&
            itemsToRender.map((item) => (
              <Field
                key={item.id}
                item={item}
                editable={editable && !item.locked}
                onSave={onSave}
                allItems={items}
                assignedUsers={entry.assignedUsers}
                dueDate={defaultDueDate}
                activityId={entry._id}
                assignedBy={entry.startedBy}
                hiddenItems={hiddenItems}
                activity={'FormEntry'}
                executedActions={executedActions}
                onExecutedActionDelete={onExecutedActionDelete}
                allCreatedActions={allCreatedActions}
                onCreatedAction={onCreatedAction}
                onDeletedAction={onDeletedAction}
                assetId={entry.assetId || null}
                activityInfo={{
                  type: 'form',
                  entryId: entry._id,
                  sectionId: section.id,
                }}
              />
            ))}
          {section.type === 'table' && isArray(section.table?.columns) && !isEmpty(section.table.columns) && (
            <>
              {section.table?.minRowsRequired && (
                <Grid container>
                  <Typography gutterBottom>
                    {rows.length}/{section.table?.minRowsRequired} required row
                    {section.table?.minRowsRequired === 1 ? '' : 's'} filled
                  </Typography>
                </Grid>
              )}
              {section.table?.settings?.viewStyle === 'form' || smallDevices ? (
                <FormStyleFieldTable
                  rows={rows}
                  onChange={onRowsSave}
                  columns={section.table.columns}
                  editable={editable}
                  allCreatedActions={allCreatedActions}
                  onCreatedAction={onCreatedAction}
                  onDeletedAction={onDeletedAction}
                  forceUpdate={tableDialogOpen}
                  tableConfig={section.table}
                  activityInfo={{
                    type: 'form',
                    entryId: entry._id,
                    sectionId: section.id,
                  }}
                  summaryComponent={
                    <>
                      {summary.map((item, i) => (
                        <Grid item key={i}>
                          <Chip
                            key={item.id}
                            label={
                              <>
                                {item.columnTitle} ({item.aggregationFunction}): <b>{item.value}</b>
                              </>
                            }
                            sx={{ mr: 1, my: 0.5 }}
                          />
                        </Grid>
                      ))}
                    </>
                  }
                />
              ) : (
                <>
                  <FieldDataGrid
                    columns={section.table.columns}
                    initial={rows}
                    editable={editable}
                    onChange={onRowsSave}
                    allCreatedActions={allCreatedActions}
                    onCreatedAction={onCreatedAction}
                    onDeletedAction={onDeletedAction}
                    forceUpdate={tableDialogOpen}
                    tableConfig={section.table}
                    activityInfo={{
                      type: 'form',
                      entryId: entry._id,
                      sectionId: section.id,
                    }}
                  />

                  <Grid container sx={{ mt: 1 }}>
                    {summary.map((item, i) => (
                      <Grid item key={i}>
                        <Chip
                          key={item.id}
                          label={
                            <>
                              {item.columnTitle} ({item.aggregationFunction}): <b>{item.value}</b>
                            </>
                          }
                          sx={{ mr: 1, my: 0.5 }}
                        />
                      </Grid>
                    ))}
                    <Button
                      startIcon={<SettingsOverscanRounded />}
                      onClick={() => setTableDialogOpen(true)}
                      sx={{ ml: 'auto' }}
                    >
                      open in dialog
                    </Button>
                  </Grid>
                  <TableDialog
                    title={section.title}
                    columns={section.table.columns}
                    rows={rows}
                    editable={editable}
                    onChange={(data) => {
                      onRowsSave(data);
                    }}
                    open={tableDialogOpen}
                    onClose={() => setTableDialogOpen(false)}
                    activityInfo={{
                      type: 'form',
                      entryId: entry._id,
                      sectionId: section.id,
                    }}
                    onCreatedAction={onCreatedAction}
                    tableConfig={section.table}
                  />
                </>
              )}
            </>
          )}
          {section.type === 'info' && section.info && (
            <Grid container>
              <Grid
                container
                item
                lg={section.info.layout === 'textAndImages' && section.info.images[0] ? 8 : 12}
                md={section.info.layout === 'textAndImages' && section.info.images[0] ? 8 : 12}
                xs={12}
                style={{ paddingRight: 8 }}
              >
                <HtmlContent content={section.info.text} />
              </Grid>
              {section.info.layout === 'textAndImages' && section.info.images[0] && (
                <Grid container item lg={4} md={4} xs={12} alignContent="flex-start">
                  <Grid container item>
                    <PicturesCarousel pictures={section.info.images} />
                  </Grid>
                  <Grid container item style={{ marginTop: 8 }}>
                    <Typography variant="h6">{section.info.caption}</Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          {section.type === 'grid' && (
            <GridSection layout={section.layout} items={section.items} editable={editable} onSave={onSave} />
          )}
        </Grid>
        {false && !section.title && editable && (
          <Grid container item alignItems="center">
            <FromNow
              prefix={saving}
              value={savedAt}
              color="textSecondary"
              style={{ fontSize: '0.75rem', marginLeft: 'auto' }}
            />
          </Grid>
        )}
      </Grid>
    </SectionContainer>
  );
}

function TableDialog({
  title,
  columns,
  rows,
  editable,
  onChange,
  open,
  onClose,
  activityInfo,
  allCreatedActions,
  onCreatedAction,
  tableConfig,
}) {
  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        <FieldDataGrid
          columns={columns}
          initial={rows}
          editable={editable}
          onChange={onChange}
          activityInfo={activityInfo}
          allCreatedActions={allCreatedActions}
          onCreatedAction={onCreatedAction}
          tableConfig={tableConfig}
          forDialog
        />
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={onClose} hideDone={!editable} />
    </RoundedDialog>
  );
}
